import React, { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import axios from "axios";
import { formatApiError } from "@/lib/api";
import { useTheme } from "@/context/ThemeContext";
import { Sun, Moon, HardHat, Mail, ArrowLeft, Send, CheckCircle2 } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function ForgotPassword() {
  const { theme, toggle } = useTheme();
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${BACKEND}/api/auth/forgot-password`, {
        email: email.trim().toLowerCase(),
      });
      setSent(true);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Request failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background text-foreground flex flex-col">
      <div className="flex items-center justify-between px-4 md:px-10 py-4 border-b-2 border-foreground dark:border-safety-yellow">
        <Link to="/" className="flex items-center gap-3">
          <span className="inline-flex items-center justify-center h-11 w-11 bg-safety-yellow text-black border-2 border-black dark:border-safety-yellow">
            <HardHat className="w-6 h-6" strokeWidth={3} />
          </span>
          <div className="leading-none">
            <div className="font-heading text-2xl md:text-3xl">KARM<span className="text-safety-yellow">.</span>SAFETY</div>
            <div className="text-[10px] md:text-xs font-bold uppercase tracking-[0.25em] text-muted-foreground mt-1">Certification Tracker v1</div>
          </div>
        </Link>
        <button data-testid="theme-toggle-forgot" onClick={toggle}
          className="h-11 w-11 inline-flex items-center justify-center border-2 border-foreground dark:border-safety-yellow hover:bg-safety-yellow hover:text-black transition-colors"
          aria-label="Toggle theme">
          {theme === "dark" ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
        </button>
      </div>

      <main className="flex-1 flex items-center justify-center p-4 md:p-10">
        <div className="brutal-card w-full max-w-md p-6 md:p-8 space-y-5">
          <div>
            <div className="inline-block bg-safety-yellow text-black px-2 py-0.5 font-bold uppercase tracking-widest text-[10px] mb-2">Account Recovery</div>
            <h1 className="font-heading text-3xl md:text-4xl">RESET PASSWORD</h1>
            <p className="text-sm text-muted-foreground mt-2">
              Enter the email on your KARM Safety account. If it matches, we'll send a one-time link that expires in 15 minutes.
            </p>
          </div>

          {sent ? (
            <div data-testid="forgot-sent" className="border-2 border-safety-yellow bg-safety-yellow/10 p-4 space-y-2">
              <div className="inline-flex items-center gap-2 font-heading text-xl">
                <CheckCircle2 className="w-5 h-5 text-safety-yellow" strokeWidth={3} /> CHECK YOUR INBOX
              </div>
              <p className="text-sm text-muted-foreground">
                If an account exists for <b className="text-foreground">{email}</b>, a reset link is on its way.
                The link is single-use and expires in 15 minutes.
              </p>
              <p className="text-xs text-muted-foreground">
                Can't find it? Check your spam folder, or request another link in a few minutes.
              </p>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-4" data-testid="forgot-form">
              <div>
                <label className="block text-xs font-bold uppercase tracking-wider mb-1.5 inline-flex items-center gap-1.5">
                  <Mail className="w-3.5 h-3.5" strokeWidth={3} /> Email
                </label>
                <input
                  data-testid="forgot-email"
                  type="email"
                  required
                  autoComplete="email"
                  autoFocus
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@company.com"
                  className="industrial-input"
                />
              </div>
              <button
                type="submit"
                data-testid="forgot-submit"
                disabled={loading}
                className="btn-primary w-full disabled:opacity-60"
              >
                <Send className="w-5 h-5 mr-2" strokeWidth={3} />
                {loading ? "Sending…" : "Send Reset Link"}
              </button>
            </form>
          )}

          <Link
            to="/"
            data-testid="back-to-login"
            className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-muted-foreground hover:text-foreground"
          >
            <ArrowLeft className="w-3.5 h-3.5" strokeWidth={3} /> Back to Sign In
          </Link>
        </div>
      </main>

      <footer className="border-t-2 border-foreground dark:border-safety-yellow py-4 px-6 text-center text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground">
        <div>© {new Date().getFullYear()} KARM LLC. OSHA-aligned certification tracking.</div>
      </footer>
    </div>
  );
}
